/**
 * Silo Activity Tracking API Module
 * 
 * Complete TypeScript implementation for the Silo Activity Tracking System.
 * Provides comprehensive activity tracking for CRM contacts and orders with:
 * - Full type safety and error handling
 * - Retry logic with exponential backoff  
 * - Caching mechanisms with TTL
 * - Authentication and authorization
 * - Performance optimizations
 * 
 * @version 2024.1.0
 * @author Silo API Team
 */

import { type RequestFunction } from './types'
import { APIError, RateLimitError, AuthenticationError, NotFoundError } from './errors'

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * Core activity interface - represents a single tracked activity
 */
export interface Activity {
  /** ISO timestamp when activity occurred */
  timestamp: string
  /** Type classification of the activity */
  activity_type: ActivityType
  /** Human-readable description of the activity */
  description: string
  /** User who performed the activity */
  user: string
  /** Additional structured data for the activity */
  metadata: ActivityMetadata
}

/**
 * Activity metadata - structured data for each activity
 */
export interface ActivityMetadata {
  /** Text description (often same as parent description) */
  text: string
  /** User who performed the activity */
  user: string
  /** Activity type (often same as parent activity_type) */
  activity: string
  /** ISO timestamp of the activity */
  timestamp: string
  /** Additional context-specific fields */
  [key: string]: any
}

/**
 * Contact-specific activity types
 */
export type ContactActivityType = 
  | 'contact_created'
  | 'contact_updated'
  | 'contact_note_added'
  | 'contact_submission_created'
  | 'contact_submission_status_changed'

/**
 * Order-specific activity types  
 */
export type OrderActivityType =
  | 'order_created'
  | 'order_status_changed'
  | 'order_note_added'
  | 'order_payment_received'
  | 'order_shipped'

/**
 * Combined activity types for unified endpoints
 */
export type ActivityType = ContactActivityType | OrderActivityType

/**
 * Activity source types for unified feed
 */
export type ActivitySource = 'contact' | 'order' | 'all'

/**
 * Base filter parameters for activity queries
 */
export interface BaseActivityFilters {
  /** Filter by specific activity type */
  activity_type?: ActivityType
  /** Filter activities after this date (YYYY-MM-DD) */
  start_date?: string
  /** Filter activities before this date (YYYY-MM-DD) */
  end_date?: string
  /** Maximum activities to return (default varies by endpoint) */
  limit?: number
}

/**
 * Extended filters for unified activity feed
 */
export interface UnifiedActivityFilters extends BaseActivityFilters {
  /** Filter by source: 'contact', 'order', or 'all' */
  source?: ActivitySource
  /** Filter by user who performed the activity */
  user?: string
}

/**
 * Contact activities response
 */
export interface ContactActivitiesResponse {
  /** Contact ID */
  contact_id: number
  /** Contact name */
  contact_name: string
  /** Total number of activities */
  total_activities: number
  /** Array of activities */
  activities: Activity[]
  /** Applied filters for reference */
  filters_applied: {
    activity_type: ActivityType | null
    start_date: string | null
    end_date: string | null
    limit: number
  }
}

/**
 * Order activities response
 */
export interface OrderActivitiesResponse {
  /** Order ID */
  order_id: number
  /** Order number display */
  order_number: string
  /** Customer name */
  customer: string
  /** Total number of activities */
  total_activities: number
  /** Array of activities */
  activities: Activity[]
  /** Applied filters for reference */
  filters_applied: {
    activity_type: ActivityType | null
    start_date: string | null
    end_date: string | null
    limit: number
  }
}

/**
 * Unified activity with source context
 */
export interface UnifiedActivity extends Activity {
  /** Source type: 'contact' or 'order' */
  source: Exclude<ActivitySource, 'all'>
  /** ID of the source entity */
  source_id: number
  /** Display name of the source entity */
  source_name: string
}

/**
 * Available filters for unified feed
 */
export interface AvailableFilters {
  /** All available activity types in the system */
  activity_types: ActivityType[]
  /** All users who have performed activities */
  users: string[]
  /** Available source types */
  sources: ActivitySource[]
}

/**
 * Unified activity feed response
 */
export interface UnifiedActivityFeedResponse {
  /** Total number of activities matching filters */
  total_activities: number
  /** Array of activities with source context */
  activities: UnifiedActivity[]
  /** Available filter options */
  available_filters: AvailableFilters
  /** Applied filters for reference */
  filters_applied: {
    source: ActivitySource
    activity_type: ActivityType | null
    user: string | null
    start_date: string | null
    end_date: string | null
    limit: number
  }
}

// ============================================================================
// CACHING SYSTEM
// ============================================================================

/**
 * Cache entry with TTL
 */
interface CacheEntry<T> {
  data: T
  timestamp: number
  ttl: number
}

/**
 * Activity-specific cache with TTL management
 */
class ActivityCache {
  private cache = new Map<string, CacheEntry<any>>()
  private defaultTTL: number

  constructor(defaultTTL = 60000) { // 1 minute default TTL
    this.defaultTTL = defaultTTL
  }

  /**
   * Generate cache key from endpoint and parameters
   */
  private getCacheKey(endpoint: string, params: Record<string, any>): string {
    const sortedParams = Object.keys(params)
      .sort()
      .reduce((result, key) => {
        result[key] = params[key]
        return result
      }, {} as Record<string, any>)
    
    return `${endpoint}?${JSON.stringify(sortedParams)}`
  }

  /**
   * Get cached data if valid
   */
  get<T>(endpoint: string, params: Record<string, any> = {}): T | null {
    const key = this.getCacheKey(endpoint, params)
    const cached = this.cache.get(key)
    
    if (cached && Date.now() - cached.timestamp < cached.ttl) {
      return cached.data as T
    }
    
    // Remove expired entries
    if (cached) {
      this.cache.delete(key)
    }
    
    return null
  }

  /**
   * Set cached data with custom TTL
   */
  set<T>(endpoint: string, params: Record<string, any> = {}, data: T, ttl?: number): void {
    const key = this.getCacheKey(endpoint, params)
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl: ttl || this.defaultTTL
    })
  }

  /**
   * Invalidate cache entries matching pattern
   */
  invalidate(pattern: string): void {
    Array.from(this.cache.keys()).forEach(key => {
      if (key.includes(pattern)) {
        this.cache.delete(key)
      }
    })
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.cache.clear()
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; entries: string[] } {
    return {
      size: this.cache.size,
      entries: Array.from(this.cache.keys())
    }
  }
}

// ============================================================================
// RETRY LOGIC WITH EXPONENTIAL BACKOFF
// ============================================================================

/**
 * Retry configuration options
 */
export interface RetryOptions {
  /** Maximum number of retry attempts */
  maxRetries?: number
  /** Initial delay in milliseconds */
  initialDelay?: number
  /** Backoff multiplier */
  backoffFactor?: number
  /** Maximum delay in milliseconds */
  maxDelay?: number
  /** Jitter factor to prevent thundering herd */
  jitterFactor?: number
}

/**
 * Default retry configuration
 */
const DEFAULT_RETRY_OPTIONS: Required<RetryOptions> = {
  maxRetries: 3,
  initialDelay: 1000,
  backoffFactor: 2,
  maxDelay: 30000,
  jitterFactor: 0.1
}

/**
 * Calculate delay with exponential backoff and jitter
 */
function calculateDelay(attempt: number, options: Required<RetryOptions>): number {
  const exponentialDelay = Math.min(
    options.initialDelay * Math.pow(options.backoffFactor, attempt),
    options.maxDelay
  )
  
  // Add jitter to prevent thundering herd effect
  const jitter = exponentialDelay * options.jitterFactor * Math.random()
  return exponentialDelay + jitter
}

/**
 * Check if error is retryable
 */
function isRetryableError(error: any): boolean {
  if (error instanceof RateLimitError) {
    return true
  }
  
  if (error instanceof APIError) {
    // Retry on server errors, but not client errors
    return error.status >= 500
  }
  
  // Retry on network errors
  return error instanceof TypeError && error.message.includes('fetch')
}

/**
 * Execute function with retry logic
 */
async function withRetry<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const config = { ...DEFAULT_RETRY_OPTIONS, ...options }
  let lastError: Error
  
  for (let attempt = 0; attempt <= config.maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      lastError = error as Error
      
      // Don't retry on the last attempt
      if (attempt === config.maxRetries) {
        break
      }
      
      // Don't retry non-retryable errors
      if (!isRetryableError(error)) {
        break
      }
      
      // Handle rate limiting with custom delay
      if (error instanceof RateLimitError && error.details?.retryAfter) {
        await new Promise(resolve => 
          setTimeout(resolve, error.details.retryAfter * 1000)
        )
        continue
      }
      
      // Wait with exponential backoff
      const delay = calculateDelay(attempt, config)
      await new Promise(resolve => setTimeout(resolve, delay))
    }
  }
  
  throw lastError
}

// ============================================================================
// ACTIVITIES API CLASS
// ============================================================================

/**
 * Activities API client with comprehensive functionality
 */
export class ActivitiesApi {
  private makeRequest: RequestFunction
  private cache: ActivityCache
  private retryOptions: RetryOptions

  constructor(
    makeRequest: RequestFunction,
    cacheOptions?: { ttl?: number },
    retryOptions?: RetryOptions
  ) {
    this.makeRequest = makeRequest
    this.cache = new ActivityCache(cacheOptions?.ttl)
    this.retryOptions = retryOptions || {}
  }

  // ============================================================================
  // CONTACT ACTIVITIES
  // ============================================================================

  /**
   * Get all activities for a specific contact
   * 
   * @param contactId - The contact ID to fetch activities for
   * @param filters - Optional filtering parameters
   * @param useCache - Whether to use caching (default: true)
   * @returns Promise with contact activities response
   * 
   * @example
   * ```typescript
   * const activities = await api.getContactActivities(54, {
   *   activity_type: 'contact_note_added',
   *   limit: 10,
   *   start_date: '2025-01-01'
   * });
   * 
   * console.log(`Found ${activities.total_activities} activities`);
   * activities.activities.forEach(activity => {
   *   console.log(`${activity.timestamp}: ${activity.description}`);
   * });
   * ```
   */
  async getContactActivities(
    contactId: number,
    filters: BaseActivityFilters = {},
    useCache = true
  ): Promise<ContactActivitiesResponse> {
    const endpoint = `/api/crm/contacts/${contactId}/activities/`
    const params = this.cleanParams(filters)
    
    // Check cache first
    if (useCache) {
      const cached = this.cache.get<ContactActivitiesResponse>(endpoint, params)
      if (cached) {
        return cached
      }
    }
    
    const response = await withRetry(
      () => this.makeRequest<ContactActivitiesResponse>(endpoint, { params }),
      this.retryOptions
    )
    
    // Cache successful response
    if (useCache) {
      this.cache.set(endpoint, params, response, 30000) // 30 second TTL for contact activities
    }
    
    return response
  }

  // ============================================================================
  // ORDER ACTIVITIES  
  // ============================================================================

  /**
   * Get all activities for a specific order
   * 
   * @param orderId - The order ID to fetch activities for
   * @param filters - Optional filtering parameters
   * @param useCache - Whether to use caching (default: true)
   * @returns Promise with order activities response
   * 
   * @example
   * ```typescript
   * const activities = await api.getOrderActivities(123, {
   *   activity_type: 'order_status_changed',
   *   limit: 20
   * });
   * 
   * // Track order status changes
   * const statusChanges = activities.activities.filter(
   *   a => a.activity_type === 'order_status_changed'
   * );
   * ```
   */
  async getOrderActivities(
    orderId: number,
    filters: BaseActivityFilters = {},
    useCache = true
  ): Promise<OrderActivitiesResponse> {
    const endpoint = `/api/ar/orders/${orderId}/activities/`
    const params = this.cleanParams(filters)
    
    // Check cache first
    if (useCache) {
      const cached = this.cache.get<OrderActivitiesResponse>(endpoint, params)
      if (cached) {
        return cached
      }
    }
    
    const response = await withRetry(
      () => this.makeRequest<OrderActivitiesResponse>(endpoint, { params }),
      this.retryOptions
    )
    
    // Cache successful response
    if (useCache) {
      this.cache.set(endpoint, params, response, 15000) // 15 second TTL for order activities (more real-time)
    }
    
    return response
  }

  // ============================================================================
  // UNIFIED ACTIVITY FEED
  // ============================================================================

  /**
   * Get activities across all contacts and orders (respects user permissions)
   * 
   * @param filters - Optional filtering and pagination parameters
   * @param useCache - Whether to use caching (default: true)  
   * @returns Promise with unified activity feed response
   * 
   * @example
   * ```typescript
   * // Get dashboard activity feed
   * const feed = await api.getUnifiedActivityFeed({
   *   source: 'all',
   *   limit: 50,
   *   start_date: '2025-09-01'
   * });
   * 
   * // Group activities by source
   * const contactActivities = feed.activities.filter(a => a.source === 'contact');
   * const orderActivities = feed.activities.filter(a => a.source === 'order');
   * 
   * // Get available filters for UI
   * const { activity_types, users } = feed.available_filters;
   * ```
   */
  async getUnifiedActivityFeed(
    filters: UnifiedActivityFilters = {},
    useCache = true
  ): Promise<UnifiedActivityFeedResponse> {
    const endpoint = '/api/activity-feed/'
    const params = this.cleanParams({
      source: 'all',
      limit: 100,
      ...filters
    })
    
    // Check cache first  
    if (useCache) {
      const cached = this.cache.get<UnifiedActivityFeedResponse>(endpoint, params)
      if (cached) {
        return cached
      }
    }
    
    const response = await withRetry(
      () => this.makeRequest<UnifiedActivityFeedResponse>(endpoint, { params }),
      this.retryOptions
    )
    
    // Cache successful response
    if (useCache) {
      this.cache.set(endpoint, params, response, 10000) // 10 second TTL for unified feed (most real-time)
    }
    
    return response
  }

  // ============================================================================
  // CONVENIENCE METHODS
  // ============================================================================

  /**
   * Get recent activities across the system (convenience method)
   * 
   * @param limit - Number of activities to return (default: 20)
   * @returns Promise with recent activities
   */
  async getRecentActivities(limit = 20): Promise<UnifiedActivity[]> {
    const response = await this.getUnifiedActivityFeed({ 
      limit,
      source: 'all' 
    })
    return response.activities
  }

  /**
   * Get activities for a specific user
   * 
   * @param user - Username to filter by
   * @param limit - Number of activities to return (default: 50)
   * @returns Promise with user activities
   */
  async getUserActivities(user: string, limit = 50): Promise<UnifiedActivity[]> {
    const response = await this.getUnifiedActivityFeed({
      user,
      limit,
      source: 'all'
    })
    return response.activities
  }

  /**
   * Get activities of a specific type across all sources
   * 
   * @param activityType - Type of activities to fetch
   * @param limit - Number of activities to return (default: 50)
   * @returns Promise with filtered activities
   */
  async getActivitiesByType(
    activityType: ActivityType,
    limit = 50
  ): Promise<UnifiedActivity[]> {
    const response = await this.getUnifiedActivityFeed({
      activity_type: activityType,
      limit,
      source: 'all'
    })
    return response.activities
  }

  /**
   * Get activities within a date range
   * 
   * @param startDate - Start date (YYYY-MM-DD format)
   * @param endDate - End date (YYYY-MM-DD format)
   * @param limit - Number of activities to return (default: 100)
   * @returns Promise with date-filtered activities
   */
  async getActivitiesInDateRange(
    startDate: string,
    endDate: string,
    limit = 100
  ): Promise<UnifiedActivity[]> {
    const response = await this.getUnifiedActivityFeed({
      start_date: startDate,
      end_date: endDate,
      limit,
      source: 'all'
    })
    return response.activities
  }

  /**
   * Get available filter options for building UI controls
   * 
   * @returns Promise with available filters
   */
  async getAvailableFilters(): Promise<AvailableFilters> {
    const response = await this.getUnifiedActivityFeed({ limit: 1 })
    return response.available_filters
  }

  // ============================================================================
  // REAL-TIME POLLING SUPPORT
  // ============================================================================

  /**
   * Poll for new activities since a given timestamp
   * Useful for real-time UI updates
   * 
   * @param sinceTimestamp - ISO timestamp to check for activities after
   * @param source - Activity source filter
   * @returns Promise with new activities
   */
  async getActivitiesSince(
    sinceTimestamp: string,
    source: ActivitySource = 'all'
  ): Promise<UnifiedActivity[]> {
    // Convert timestamp to date for API
    const sinceDate = new Date(sinceTimestamp).toISOString().split('T')[0]
    
    const response = await this.getUnifiedActivityFeed({
      start_date: sinceDate,
      source,
      limit: 500 // Get more to ensure we capture all new activities
    }, false) // Don't use cache for real-time polling
    
    // Filter to only activities newer than the timestamp
    return response.activities.filter(activity => 
      activity.timestamp > sinceTimestamp
    )
  }

  // ============================================================================
  // CACHE MANAGEMENT
  // ============================================================================

  /**
   * Clear activity cache (useful after data mutations)
   * 
   * @param pattern - Optional pattern to match specific cache keys
   */
  clearCache(pattern?: string): void {
    if (pattern) {
      this.cache.invalidate(pattern)
    } else {
      this.cache.clear()
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { size: number; entries: string[] } {
    return this.cache.getStats()
  }

  /**
   * Invalidate cache for specific contact
   * 
   * @param contactId - Contact ID to invalidate cache for
   */
  invalidateContactCache(contactId: number): void {
    this.cache.invalidate(`/api/crm/contacts/${contactId}/activities/`)
    this.cache.invalidate('/api/activity-feed/') // Also clear unified feed
  }

  /**
   * Invalidate cache for specific order
   * 
   * @param orderId - Order ID to invalidate cache for
   */
  invalidateOrderCache(orderId: number): void {
    this.cache.invalidate(`/api/ar/orders/${orderId}/activities/`)
    this.cache.invalidate('/api/activity-feed/') // Also clear unified feed
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Clean up parameters by removing null/undefined values
   */
  private cleanParams(params: Record<string, any>): Record<string, any> {
    const cleaned: Record<string, any> = {}
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        cleaned[key] = value
      }
    })
    
    return cleaned
  }

  /**
   * Validate date format (YYYY-MM-DD)
   */
  private validateDateFormat(date: string): boolean {
    return /^\d{4}-\d{2}-\d{2}$/.test(date)
  }

  /**
   * Format date to API-expected format
   */
  formatDateForAPI(date: Date | string): string {
    if (date instanceof Date) {
      return date.toISOString().split('T')[0]
    }
    
    // Validate existing string format
    if (!this.validateDateFormat(date)) {
      throw new Error('Date must be in YYYY-MM-DD format')
    }
    
    return date
  }

  /**
   * Get relative time string (e.g., "5 minutes ago")
   */
  getRelativeTime(timestamp: string): string {
    const now = new Date()
    const activityTime = new Date(timestamp)
    const diffMs = now.getTime() - activityTime.getTime()
    const diffMinutes = Math.floor(diffMs / (1000 * 60))
    const diffHours = Math.floor(diffMinutes / 60)
    const diffDays = Math.floor(diffHours / 24)
    
    if (diffMinutes < 1) {
      return 'just now'
    } else if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`
    } else {
      return activityTime.toLocaleDateString()
    }
  }
}

// ============================================================================
// ACTIVITY POLLING CLASS FOR REAL-TIME UPDATES
// ============================================================================

/**
 * Activity poller for real-time UI updates with intelligent backoff
 */
class ActivityPoller {
  private api: ActivitiesApi
  private onUpdate: (activities: UnifiedActivity[]) => void
  private interval: number
  private maxInterval: number
  private lastActivityTimestamp: string | null = null
  private timer: NodeJS.Timeout | null = null
  private isActive = false

  constructor(
    api: ActivitiesApi,
    onUpdate: (activities: UnifiedActivity[]) => void,
    options: {
      initialInterval?: number
      maxInterval?: number
    } = {}
  ) {
    this.api = api
    this.onUpdate = onUpdate
    this.interval = options.initialInterval || 5000 // 5 seconds
    this.maxInterval = options.maxInterval || 60000 // 60 seconds
  }

  /**
   * Start polling for activity updates
   */
  start(): void {
    if (this.isActive) {
      return
    }
    
    this.isActive = true
    this.poll()
  }

  /**
   * Stop polling
   */
  stop(): void {
    this.isActive = false
    
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  /**
   * Internal polling method with intelligent backoff
   */
  private async poll(): Promise<void> {
    if (!this.isActive) {
      return
    }
    
    try {
      const activities = await this.api.getRecentActivities(20)
      
      if (activities.length > 0) {
        const newestTimestamp = activities[0].timestamp
        
        if (this.lastActivityTimestamp && newestTimestamp > this.lastActivityTimestamp) {
          // New activities found - notify and reset to fast polling
          const newActivities = activities.filter(
            a => a.timestamp > this.lastActivityTimestamp!
          )
          this.onUpdate(newActivities)
          this.interval = 5000 // Reset to fast polling
        } else {
          // No new activities - slow down polling
          this.interval = Math.min(this.interval * 1.5, this.maxInterval)
        }
        
        this.lastActivityTimestamp = newestTimestamp
      }
      
    } catch (error) {
      console.error('Activity polling error:', error)
      // Slow down polling on error
      this.interval = Math.min(this.interval * 2, this.maxInterval)
    }
    
    // Schedule next poll
    if (this.isActive) {
      this.timer = setTimeout(() => this.poll(), this.interval)
    }
  }

  /**
   * Get current polling status
   */
  getStatus(): {
    isActive: boolean
    currentInterval: number
    lastActivityTimestamp: string | null
  } {
    return {
      isActive: this.isActive,
      currentInterval: this.interval,
      lastActivityTimestamp: this.lastActivityTimestamp
    }
  }
}

// ============================================================================
// ACTIVITY PAGINATOR FOR LARGE DATASETS
// ============================================================================

/**
 * Activity paginator for handling large datasets efficiently
 */
class ActivityPaginator {
  private api: ActivitiesApi
  private endpoint: 'contact' | 'order' | 'unified'
  private entityId?: number
  private baseFilters: Record<string, any>
  private pageSize: number
  private activities: UnifiedActivity[] = []
  private hasMore = true
  private oldestTimestamp: string | null = null

  constructor(
    api: ActivitiesApi,
    endpoint: 'contact' | 'order' | 'unified',
    options: {
      entityId?: number
      filters?: Record<string, any>
      pageSize?: number
    } = {}
  ) {
    this.api = api
    this.endpoint = endpoint
    this.entityId = options.entityId
    this.baseFilters = options.filters || {}
    this.pageSize = options.pageSize || 50
  }

  /**
   * Fetch the next page of activities
   */
  async fetchNextPage(): Promise<UnifiedActivity[]> {
    if (!this.hasMore) {
      return []
    }

    const filters: any = { ...this.baseFilters, limit: this.pageSize }
    
    // Use oldest timestamp as end_date for next page
    if (this.oldestTimestamp) {
      const endDate = new Date(this.oldestTimestamp)
      endDate.setSeconds(endDate.getSeconds() - 1) // Go back 1 second to avoid duplicates
      filters.end_date = endDate.toISOString().split('T')[0]
    }

    let newActivities: UnifiedActivity[]

    try {
      if (this.endpoint === 'contact' && this.entityId) {
        const response = await this.api.getContactActivities(this.entityId, filters)
        // Convert contact activities to unified format
        newActivities = response.activities.map(activity => ({
          ...activity,
          source: 'contact' as const,
          source_id: response.contact_id,
          source_name: response.contact_name
        }))
      } else if (this.endpoint === 'order' && this.entityId) {
        const response = await this.api.getOrderActivities(this.entityId, filters)
        // Convert order activities to unified format  
        newActivities = response.activities.map(activity => ({
          ...activity,
          source: 'order' as const,
          source_id: response.order_id,
          source_name: response.order_number
        }))
      } else {
        const response = await this.api.getUnifiedActivityFeed(filters)
        newActivities = response.activities
      }
      
      // Check if we have fewer results than requested (indicates end of data)
      if (newActivities.length < this.pageSize) {
        this.hasMore = false
      }

      // Update oldest timestamp for next page
      if (newActivities.length > 0) {
        this.oldestTimestamp = newActivities[newActivities.length - 1].timestamp
        this.activities.push(...newActivities)
      }

      return newActivities
      
    } catch (error) {
      console.error('Error fetching activity page:', error)
      throw error
    }
  }

  /**
   * Get all loaded activities
   */
  getAllActivities(): UnifiedActivity[] {
    return [...this.activities]
  }

  /**
   * Reset paginator to start over
   */
  reset(): void {
    this.activities = []
    this.hasMore = true
    this.oldestTimestamp = null
  }

  /**
   * Get pagination status
   */
  getStatus(): {
    totalLoaded: number
    hasMore: boolean
    oldestTimestamp: string | null
  } {
    return {
      totalLoaded: this.activities.length,
      hasMore: this.hasMore,
      oldestTimestamp: this.oldestTimestamp
    }
  }
}

// ============================================================================
// EXPORTS
// ============================================================================

// Export utility classes
export { ActivityCache, ActivityPoller, ActivityPaginator }

/**
 * Activity type constants for easy reference
 */
export const CONTACT_ACTIVITY_TYPES: ContactActivityType[] = [
  'contact_created',
  'contact_updated', 
  'contact_note_added',
  'contact_submission_created',
  'contact_submission_status_changed'
]

export const ORDER_ACTIVITY_TYPES: OrderActivityType[] = [
  'order_created',
  'order_status_changed',
  'order_note_added',
  'order_payment_received',
  'order_shipped'
]

export const ALL_ACTIVITY_TYPES: ActivityType[] = [
  ...CONTACT_ACTIVITY_TYPES,
  ...ORDER_ACTIVITY_TYPES
]

/**
 * Default configurations for common use cases
 */
export const ACTIVITY_CONFIGS = {
  DASHBOARD: {
    limit: 50,
    cacheOptions: { ttl: 10000 }, // 10 seconds
    retryOptions: { maxRetries: 2 }
  },
  CONTACT_DETAIL: {
    limit: 100,
    cacheOptions: { ttl: 30000 }, // 30 seconds
    retryOptions: { maxRetries: 3 }
  },
  ORDER_DETAIL: {
    limit: 100,
    cacheOptions: { ttl: 15000 }, // 15 seconds  
    retryOptions: { maxRetries: 3 }
  },
  REAL_TIME: {
    limit: 20,
    cacheOptions: { ttl: 5000 }, // 5 seconds
    retryOptions: { maxRetries: 1 }
  }
} as const